import type { FC } from 'react';
import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

import * as programActionCreators from '@makemydeal/dr-dash-store/actions/programActionCreators';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import {
    buildIncentivesContextId,
    buildProgramContextId,
    buildProgramContextIdFromTerm,
    isSameProgramRow
} from '@makemydeal/dr-dash-store/utils/programUtils';

import type { ProgramQuote, ProgramsSortSelection } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';

import { createSelectActionColumn } from './SelectActionColumn';
import { ProgramsTable } from './ProgramsTable';
import { createProgramTableData, findAppliedProgramKey, pickByRequestedTier, sortByDealDefaults } from './utils';
import { useNestedIncentivesTableData } from './useNestedIncentivesTableData';
import { NestedIncentivesTable } from './NestedIncentivesTable';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

type ProgramQuotes = Array<ProgramQuote & { key: string }>;

type TermProfit = { frontEndProfit?: number; backEndProfit?: number; totalProfit?: number };

type CommittedAmounts = { contextId: string; payment: number; rebates?: number };

const PAYMENT_COMMIT_DELAY_MS = 300;

const buildTermSourcedRowValues = (monthlyPayment?: number, rebates?: number, profit?: TermProfit): Record<string, string> => ({
    ...(monthlyPayment != null && { monthlyPayment: formatDollarsAndCents(monthlyPayment) }),
    ...(rebates != null && { appliedIncentiveAmount: formatDollarsAndCents(rebates) }),
    ...(profit?.frontEndProfit != null && { frontEndProfit: formatDollarsAndCents(profit.frontEndProfit) }),
    ...(profit?.backEndProfit != null && { backEndProfit: formatDollarsAndCents(profit.backEndProfit) }),
    ...(profit?.totalProfit != null && { totalProfit: formatDollarsAndCents(profit.totalProfit) })
});

const isSameRequestedTier = (pinned: ProgramQuote, pending: ProgramQuote): boolean =>
    pending.requestedTier == null || pending.requestedTier === pinned.requestedTier;

const isPendingSamePinnedProgram = (pinned: ProgramQuote, pending: ProgramQuote): boolean =>
    buildIncentivesContextId(pinned) === buildIncentivesContextId(pending) && isSameRequestedTier(pinned, pending);

const applyTermSourcedRowValues = (rows: ProgramQuotes, appliedKey: string, values: Record<string, string>): ProgramQuotes =>
    appliedKey && Object.keys(values).length > 0 ? rows.map((row) => (row.key === appliedKey ? { ...row, ...values } : row)) : rows;

type ProgramsListViewProps = {
    data: ProgramQuote[];
    offerType: OfferType;
    dealTier?: string;
    reorderTrigger?: number;
    recalculating?: boolean;
    sortPreference?: ProgramsSortSelection | null;
    onSortChange?: (sort: ProgramsSortSelection | null) => void;
};

export const ProgramsListCompactUI: FC<ProgramsListViewProps> = ({
    data,
    offerType,
    dealTier,
    reorderTrigger = 0,
    recalculating = false,
    sortPreference,
    onSortChange
}) => {
    const selectedTerm = useSelector((state) => offerReduxSelectors.getSelectedTerm(state, offerType));
    const retainedProgram = useSelector(programsSelectors.getRetainedProgramDisplay);
    const allTiersMode = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isCashFixesEnabled = useSelector(featureToggleSelectors.isCashDealIncentivesFixesEnabled);
    const isStickyFiltersAndSortingEnabled = useSelector(featureToggleSelectors.isStickyFiltersAndSortingEnabled);
    const dispatch = useDispatch();

    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);
    const selectedProgram = useSelector(programsSelectors.getSelectedProgram);
    const pendingApplyProgram = useSelector(programsSelectors.getPendingApplyProgram);
    const allProgramsLoaded = useSelector(programsSelectors.getAllProgramsLoaded);
    const { nestedIncentivesTableData } = useNestedIncentivesTableData();

    const sortPinRef = useRef<{ tier?: string; term?: number } | undefined>(undefined);
    const prevSortTriggerRef = useRef(reorderTrigger);
    const liveTerm = selectedTerm.months;
    const isDealSortActive = isStickyFiltersAndSortingEnabled && offerType !== CASH;
    const sortTriggerChanged = prevSortTriggerRef.current !== reorderTrigger;
    prevSortTriggerRef.current = reorderTrigger;
    if (!isDealSortActive) {
        sortPinRef.current = undefined;
    } else if (sortTriggerChanged) {
        sortPinRef.current = { tier: dealTier, term: liveTerm };
    } else if (!sortPinRef.current) {
        if (liveTerm !== undefined) sortPinRef.current = { tier: dealTier, term: liveTerm };
    } else if (sortPinRef.current.tier === undefined && dealTier !== undefined) {
        sortPinRef.current = { tier: dealTier, term: sortPinRef.current.term };
    }
    const sortPin = sortPinRef.current;
    const sortPinTier = sortPin ? sortPin.tier : dealTier;
    const sortPinTerm = sortPin ? sortPin.term : liveTerm;

    const dealCentralTableData: ProgramQuotes = useMemo(() => {
        const rows = createProgramTableData(data, allTiersMode);
        return isDealSortActive ? sortByDealDefaults(rows, sortPinTier, sortPinTerm) : rows;
    }, [data, allTiersMode, isDealSortActive, sortPinTier, sortPinTerm]);

    const pendingProgram = pendingApplyProgram?.program;
    const effectiveTier = pendingProgram?.requestedTier ?? dealTier;

    const appliedProgramKey = useMemo(
        () => findAppliedProgramKey(dealCentralTableData, selectedTerm, offerType, selectedProgram, retainedProgram, effectiveTier),
        [dealCentralTableData, selectedTerm, offerType, selectedProgram, retainedProgram, effectiveTier]
    );

    const orderingKeyRef = useRef<string>('');
    const initialPinPendingRef = useRef(true);
    const prevReorderTriggerRef = useRef(reorderTrigger);
    if (prevReorderTriggerRef.current !== reorderTrigger) {
        prevReorderTriggerRef.current = reorderTrigger;
        orderingKeyRef.current = appliedProgramKey;
        initialPinPendingRef.current = false;
    } else if (initialPinPendingRef.current && appliedProgramKey) {
        initialPinPendingRef.current = false;
        orderingKeyRef.current = appliedProgramKey;
    }
    const orderingKey = orderingKeyRef.current;

    const pinnedRow = useMemo(
        () => dealCentralTableData.find((row) => row.key === orderingKey),
        [dealCentralTableData, orderingKey]
    );

    const pendingApplyKey = useMemo(() => {
        if (!pendingApplyProgram?.programContextId || !pendingProgram) return '';
        if (allTiersMode && pinnedRow && isPendingSamePinnedProgram(pinnedRow, pendingProgram)) {
            return pinnedRow.key;
        }
        const matches = dealCentralTableData.filter((row) => isSameProgramRow(row, pendingProgram, offerType));
        return pickByRequestedTier(matches, pendingProgram.requestedTier)?.key ?? '';
    }, [dealCentralTableData, pendingApplyProgram, pendingProgram, offerType, pinnedRow, allTiersMode]);

    const orderedTableData = useMemo(() => {
        if (!allTiersMode) return dealCentralTableData;
        const pinKey = dealCentralTableData.some((row) => row.key === orderingKey) ? orderingKey : pendingApplyKey;
        if (!pinKey) return dealCentralTableData;
        const index = dealCentralTableData.findIndex((row) => row.key === pinKey);
        if (index <= 0) return dealCentralTableData;
        return [dealCentralTableData[index], ...dealCentralTableData.slice(0, index), ...dealCentralTableData.slice(index + 1)];
    }, [dealCentralTableData, orderingKey, pendingApplyKey, allTiersMode]);

    const hasAppliedProgram = Boolean(retainedProgram?.programName || selectedTerm.lenderProgram?.programName);
    const lastAppliedProgramKeyRef = useRef('');
    if (appliedProgramKey) {
        lastAppliedProgramKeyRef.current = appliedProgramKey;
    }
    const lastAppliedKeyInData = dealCentralTableData.some((row) => row.key === lastAppliedProgramKeyRef.current);
    const stableAppliedProgramKey =
        appliedProgramKey ||
        (hasAppliedProgram && !pendingApplyKey && lastAppliedKeyInData ? lastAppliedProgramKeyRef.current : '');

    const expandedKey = pendingApplyKey || stableAppliedProgramKey;

    const expandedProgramIdentity = useMemo(() => {
        const expandedRow = dealCentralTableData.find((row) => row.key === expandedKey);
        return buildProgramContextId(expandedRow) || expandedKey;
    }, [dealCentralTableData, expandedKey]);

    const appliedProgramContextId = buildProgramContextIdFromTerm(selectedTerm);

    const quote = useSelector(programsSelectors.getProgramFromCurrentOffer);

    useEffect(() => {
        if (quote && appliedProgramKey) {
            dispatch(programActionCreators.selectProgramQuote(quote));
        }
    }, [appliedProgramKey, dispatch]);

    const isError = useSelector(programsSelectors.getIncentivesLoadingError);
    const isApplyingProgram = Boolean(pendingApplyKey);
    const isLoading = Boolean(quote && isCalculatingPayment) && !recalculating;
    const isProgramsLoading = allTiersMode && !allProgramsLoaded;

    const nestedIncentivesPending = isApplyingProgram || isLoading || isProgramsLoading;
    const lastNestedIncentivesRef = useRef(nestedIncentivesTableData);
    if (nestedIncentivesTableData.length > 0) {
        lastNestedIncentivesRef.current = nestedIncentivesTableData;
    }
    const displayNestedIncentives =
        allTiersMode && nestedIncentivesTableData.length === 0 && nestedIncentivesPending
            ? lastNestedIncentivesRef.current
            : nestedIncentivesTableData;

    const liveMonthlyPayment = selectedTerm.monthlyPayment as number | undefined;
    const appliedPaymentBaseline = useSelector(programsSelectors.getAppliedPaymentBaseline);
    const stickyAppliedContextId = appliedProgramContextId || appliedPaymentBaseline?.contextId || '';

    const liveRebates = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForDisplay) as number | undefined;

    const [committed, setCommitted] = useState<CommittedAmounts | undefined>(undefined);
    useEffect(() => {
        // This "commit" freezes the displayed payment/rebates briefly after the multi-tier fan-out
        // settles - useful for finance/lease/balloon, not cash (no term to fan out across). The
        // offerType === CASH check is redundant with liveMonthlyPayment == null today (cash's term is
        // always {}), but kept as an explicit guard rather than relying on that always holding; see the
        // offerType === CASH branch in displayTableData below for cash's actual Rebates fix.
        if (!allTiersMode || (offerType === CASH && isCashFixesEnabled) || recalculating || liveMonthlyPayment == null) {
            return undefined;
        }
        const timer = setTimeout(() => {
            setCommitted({ contextId: stickyAppliedContextId, payment: liveMonthlyPayment, rebates: liveRebates });
        }, PAYMENT_COMMIT_DELAY_MS);
        return () => clearTimeout(timer);
    }, [allTiersMode, offerType, isCashFixesEnabled, recalculating, liveMonthlyPayment, liveRebates, stickyAppliedContextId]);

    const committedForContext = committed?.contextId === stickyAppliedContextId ? committed : undefined;
    const committedPayment = committedForContext?.payment;
    const committedRebates = committedForContext?.rebates;

    const baselineContextId = appliedPaymentBaseline?.contextId;
    useEffect(() => {
        if (allTiersMode && committedPayment != null && stickyAppliedContextId && baselineContextId !== stickyAppliedContextId) {
            dispatch(programActionCreators.setAppliedPaymentBaseline(stickyAppliedContextId, committedPayment, committedRebates));
        }
    }, [allTiersMode, committedPayment, committedRebates, stickyAppliedContextId, baselineContextId, dispatch]);
    useEffect(
        () => () => {
            if (allTiersMode) dispatch(programActionCreators.clearAppliedPaymentBaseline());
        },
        [allTiersMode, dispatch]
    );

    const matchedBaseline =
        appliedPaymentBaseline && appliedPaymentBaseline.contextId === stickyAppliedContextId ? appliedPaymentBaseline : undefined;
    const baselinePayment = matchedBaseline?.amount;
    const baselineRebates = matchedBaseline?.rebates;
    const displayMonthlyPayment = committedPayment ?? liveMonthlyPayment;
    const displayRebates = committedRebates ?? liveRebates;
    const resolvedAppliedRowKey =
        appliedProgramKey || (dealCentralTableData.find((row) => buildProgramContextId(row) === stickyAppliedContextId)?.key ?? '');
    const appliedRowKey = pendingApplyKey ? '' : stableAppliedProgramKey || resolvedAppliedRowKey;

    const effectiveExpandedKey = expandedKey || resolvedAppliedRowKey;
    const effectiveExpandedIdentity = expandedProgramIdentity || stickyAppliedContextId;

    const termProfit = selectedTerm.profit as TermProfit | undefined;
    const displayTableData = useMemo(() => {
        // Cash always has exactly one row (programsReducer.ts synthesizes it - cash has no per-lender
        // program list), and that row's own appliedIncentiveAmount is only ever set once, at the initial
        // program-orchestrator catalog load - nothing re-dispatches REQUEST_PROGRAMS_SUCCESS after a
        // checkbox toggle or a manual rebate add, so it can never reflect those on its own. Overlay the
        // live total directly here, unconditionally, rather than relying on allTiersMode/appliedRowKey
        // matching (designed for finance/lease's multi-tier fan-out, not cash's single row) to carry it -
        // this guarantees the Selected row's Rebates always equals RebatesTotals' Incentives Total, since
        // both read the exact same selector.
        if (offerType === CASH && isCashFixesEnabled) {
            // Match on the row's own identity rather than position - the single-row invariant this
            // relies on holds today (see comment above) but isn't enforced, and a response that did
            // yield more than one row should leave them alone rather than silently overlay the live
            // total onto whichever one happens to sort first.
            if (liveRebates == null || orderedTableData.length !== 1) {
                return orderedTableData;
            }
            return orderedTableData.map((row) => ({ ...row, appliedIncentiveAmount: formatDollarsAndCents(liveRebates) }));
        }
        return allTiersMode
            ? applyTermSourcedRowValues(
                  orderedTableData,
                  appliedRowKey,
                  buildTermSourcedRowValues(displayMonthlyPayment, displayRebates, termProfit)
              )
            : orderedTableData;
    }, [
        offerType,
        isCashFixesEnabled,
        liveRebates,
        allTiersMode,
        orderedTableData,
        appliedRowKey,
        displayMonthlyPayment,
        displayRebates,
        termProfit
    ]);

    const renderSelect = useMemo(
        () => createSelectActionColumn(stableAppliedProgramKey, pendingApplyKey),
        [stableAppliedProgramKey, pendingApplyKey]
    );

    const liveAmounts = useMemo(
        () =>
            allTiersMode
                ? {
                      appliedKey: appliedRowKey,
                      payment: displayMonthlyPayment,
                      baselinePayment,
                      rebates: displayRebates,
                      baselineRebates
                  }
                : undefined,
        [allTiersMode, appliedRowKey, displayMonthlyPayment, baselinePayment, displayRebates, baselineRebates]
    );

    const renderExpandedRow = useCallback(
        () => (
            <NestedIncentivesTable
                key={effectiveExpandedIdentity}
                nestedIncentivesTableData={displayNestedIncentives}
                isError={isError}
                isLoading={isLoading}
                isEmpty={!isApplyingProgram && displayNestedIncentives.length === 0}
                isProgramsLoading={isProgramsLoading}
                isApplying={isApplyingProgram}
            />
        ),
        [effectiveExpandedIdentity, displayNestedIncentives, isError, isLoading, isApplyingProgram, isProgramsLoading]
    );

    return (
        <ProgramsTable
            testId="programs-list-view-for-deal-central"
            data={displayTableData}
            offerType={offerType}
            renderSelect={renderSelect}
            pinnedKey={allTiersMode ? orderingKey : stableAppliedProgramKey}
            expandedKey={effectiveExpandedKey}
            renderExpandedRow={renderExpandedRow}
            liveAmounts={liveAmounts}
            sortPreference={sortPreference}
            onSortChange={onSortChange}
        />
    );
};
